import { observer } from "mobx-react-lite";
import { CheckCircle2, Clock, FileText, Shield } from "lucide-react";
import {
  useESGCollaborationStore,
  useESGDataEntryStore,
  useESGDisclosureSelectionStore,
} from "@/features/esg/hooks/useESGReportStore";
import { useTran } from "@/hooks";

const ReviewStatsGrid = observer(() => {
  const { t } = useTran();
  const { filteredSelectedCodesByCategory } = useESGDisclosureSelectionStore();
  const { completedCount, progressPercentage } = useESGDataEntryStore();
  const { delegatedDisclosures } = useESGCollaborationStore();

  const categoryCount = Object.keys(filteredSelectedCodesByCategory).filter(
    (category) => filteredSelectedCodesByCategory[category]?.length > 0,
  ).length;
  const totalSelected = Object.values(filteredSelectedCodesByCategory).flat()
    .length;
  const pendingCount = Math.max(totalSelected - completedCount, 0);
  const delegatedCount = delegatedDisclosures.size;

  const activePillars = Object.keys(filteredSelectedCodesByCategory).filter(
    (cat) => filteredSelectedCodesByCategory[cat]?.length > 0,
  );

  return (
    <div className="grid h-full grid-cols-2 gap-3">
      {/* Total disclosures */}
      <div className="rounded-radius border border-border bg-card p-4 shadow-sm">
        <div className="flex items-center gap-3">
          <div className="flex h-9 w-9 items-center justify-center rounded-radius bg-muted">
            <FileText className="h-4 w-4 text-muted-foreground" />
          </div>
          <div>
            <p className="text-xs text-muted-foreground">
              {t("Total disclosures")}
            </p>
            <p className="text-xl font-semibold text-foreground">
              {totalSelected}
            </p>
            <p className="text-[11px] text-muted-foreground">
              {t("Across {{count}} pillars", { count: categoryCount })}
            </p>
          </div>
        </div>
      </div>

      {/* Completed + Pending side by side */}
      <div className="rounded-radius border border-border bg-card p-4 shadow-sm">
        <div className="flex items-center gap-1.5">
          <CheckCircle2 className="h-4 w-4 text-emerald-500" />
          <p className="text-xs text-muted-foreground">{t("Completed")}</p>
        </div>
        <p className="mt-1 text-xl font-semibold text-foreground">
          {completedCount}
        </p>
        <p className="text-[11px] text-muted-foreground">
          {progressPercentage}% {t("overall")}
        </p>
      </div>
      <div className="rounded-radius border border-border bg-card p-4 shadow-sm">
        <div className="flex items-center gap-1.5">
          <Clock className="h-4 w-4 text-amber-500" />
          <p className="text-xs text-muted-foreground">{t("Pending")}</p>
        </div>
        <p className="mt-1 text-xl font-semibold text-foreground">
          {pendingCount}
        </p>
        <p className="text-[11px] text-muted-foreground">
          {delegatedCount} {t("delegated")}
        </p>
      </div>

      {/* Categories covered */}
      <div className="rounded-radius border border-border bg-card p-4 shadow-sm">
        <div className="flex items-center gap-3">
          <div className="flex h-9 w-9 items-center justify-center rounded-radius bg-muted">
            <Shield className="h-4 w-4 text-muted-foreground" />
          </div>
          <div>
            <p className="text-xs text-muted-foreground">
              {t("Categories covered")}
            </p>
            <p className="text-xl font-semibold text-foreground">
              {categoryCount}
            </p>
            <p className="text-[11px] text-muted-foreground">
              {activePillars.map((p) => t(p)).join(" · ")}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
});

export default ReviewStatsGrid;
